import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import Input from "../../components/Form/Input/input";
import { InfoSectionMessage } from "../../components/Form/Label/styles";
import { FieldHint, Form, FormGroup } from "../../components/Form/styles";
import Modal, { ModalButton, ModalSecondaryButton } from "../../components/Modal";
import { isApiError } from "../../services/api/errors";
import { PaymentBalance, PaymentWithdrawal, withdraw } from "../../services/payments";
import { centsToField, currencySymbol, fieldToCents, formatCents } from "../Agenda/format";

const prefix = "pages.settings.payments.withdrawModal";

/**
 * Local only (`yarn dev`, never a build): Sacar opens even without balance, the modal uses a test balance and
 * confirming pretends it went through without calling the API — to try the screen, since nothing can be withdrawn
 * locally.
 */
export const WITHDRAW_TEST_MODE = import.meta.env.DEV && import.meta.env.MODE !== "test";
const TEST_AVAILABLE_CENTS = 50000;

/** The amount as money is typed (the service price's way): digits fill from the cents ("4500" → 45,00). */
const maskAmount = (text: string) => {
  const digits = text.replace(/\D/g, "").replace(/^0+/, "").slice(0, 9);
  return digits ? centsToField(Number(digits)) : "";
};

/** The API's refusals that have their own text; anything else is the generic one. */
const ERRORS: Record<string, string> = {
  INSUFFICIENT_BALANCE: "insufficient",
  BELOW_MINIMUM: "belowMinimum",
  CHARGEBACK_OPEN: "chargeback",
  WITHDRAWAL_IN_PROGRESS: "inProgress",
};

type Props = {
  siteId: string;
  balance: PaymentBalance;
  /** "Bradesco ••24": the account the money goes to */
  bankLabel: string | null;
  /** Requested: the screen reloads the balance and the history. */
  onDone: (withdrawal: PaymentWithdrawal) => void;
  onClose: () => void;
};

/** Sacar: the owner types what leaves the balance; the fee comes out of it, and the rest goes to the bank. */
export const WithdrawModal = ({ siteId, balance: realBalance, bankLabel, onDone, onClose }: Props) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState("");
  const [error, setError] = useState<string>();
  const [isSaving, setSaving] = useState(false);

  const isTest = WITHDRAW_TEST_MODE && realBalance.availableCents < realBalance.minWithdrawCents;
  const balance = isTest ? { ...realBalance, availableCents: TEST_AVAILABLE_CENTS } : realBalance;
  const money = (cents: number) => formatCents(cents, "BRL");
  const cents = fieldToCents(amount);
  const typed = cents !== null && Number.isFinite(cents) ? cents : 0;
  // never below zero (an amount under the fee is refused anyway)
  const net = Math.max(0, typed - balance.withdrawFeeCents);
  const isValid =
    cents !== null &&
    Number.isInteger(cents) &&
    cents >= balance.minWithdrawCents &&
    cents <= balance.availableCents &&
    cents > balance.withdrawFeeCents;

  // what's wrong with the amount typed (nothing typed: nothing yet), said as it's typed
  const problem: "insufficient" | "belowMinimum" | null =
    cents === null || cents === 0
      ? null
      : cents > balance.availableCents
        ? "insufficient"
        : cents < balance.minWithdrawCents || cents <= balance.withdrawFeeCents
          ? "belowMinimum"
          : null;

  const submit = async () => {
    if (!isValid || isSaving || cents === null) return;
    setError(undefined);
    setSaving(true);
    if (isTest) {
      // local test: as if Pagar.me took it
      await new Promise(resolve => setTimeout(resolve, 600));
      onDone({
        id: "test",
        status: "pending",
        amountCents: cents,
        feeCents: balance.withdrawFeeCents,
        netCents: cents - balance.withdrawFeeCents,
        requestedAt: new Date().toISOString(),
      });
      return;
    }
    try {
      const withdrawal = await withdraw(siteId, cents);
      // Pagar.me refused it right away: nothing left the balance
      if (withdrawal.status === "failed") {
        setError(t(`${prefix}.errors.generic`));
        setSaving(false);
        return;
      }
      onDone(withdrawal);
    } catch (failure) {
      setSaving(false);
      const code = Object.keys(ERRORS).find(key => isApiError(failure, key));
      setError(t(`${prefix}.errors.${code ? ERRORS[code] : "generic"}`, { min: money(balance.minWithdrawCents) }));
    }
  };

  return (
    <Modal
      title={t(`${prefix}.title`)}
      subtitle={bankLabel ? t(`${prefix}.subtitleWithBank`, { bank: bankLabel }) : t(`${prefix}.subtitle`)}
      escapeExit
      busy={isSaving}
      onExit={onClose}
      error={error}
      footer={
        <>
          <ModalSecondaryButton type="button" disabled={isSaving} onClick={onClose}>
            {t(`${prefix}.cancel`)}
          </ModalSecondaryButton>
          <ModalButton loading={isSaving} disabled={!isValid} onClick={submit}>
            {t(`${prefix}.confirm`)}
          </ModalButton>
        </>
      }>
      <Form
        noValidate
        onSubmit={event => {
          event.preventDefault();
          submit();
        }}>
        <Fields>
          {isTest && <TestNote>{t(`${prefix}.testMode`, { amount: money(TEST_AVAILABLE_CENTS) })}</TestNote>}
          <FormGroup>
            <Input
              id="withdraw-amount"
              type="text"
              label={t(`${prefix}.amount`)}
              prefix={currencySymbol("BRL")}
              placeholder={centsToField(0)}
              value={amount}
              ignoreLengthInfo
              // on the right of the label: what can be withdrawn
              infoMessage={t(`${prefix}.limits`, {
                available: money(balance.availableCents),
                min: money(balance.minWithdrawCents),
              })}
              onChange={event => {
                setAmount(maskAmount(event.target.value));
                setError(undefined);
              }}
            />
            {/* right under the field, as the other fields' errors */}
            {problem && (
              <Problem role="alert">
                {t(`${prefix}.errors.${problem}`, { min: money(balance.minWithdrawCents) })}
              </Problem>
            )}
          </FormGroup>
          {/* always there, from zero: the amount, the fee and what reaches the bank */}
          <Summary role="group" aria-label={t(`${prefix}.summary`)}>
            <div>
              <dt>{t(`${prefix}.amount`)}</dt>
              <dd>{money(typed)}</dd>
            </div>
            <div>
              <dt>{t(`${prefix}.fee`)}</dt>
              <dd>− {money(balance.withdrawFeeCents)}</dd>
            </div>
            <div className="total">
              <dt>{t(`${prefix}.net`)}</dt>
              <dd>{money(net)}</dd>
            </div>
          </Summary>
        </Fields>
      </Form>
    </Modal>
  );
};

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  ${FormGroup} {
    margin-bottom: 0;
  }

  /* what can be withdrawn, on the right of the field's label: information, not an error */
  ${InfoSectionMessage} {
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--color-neutral-150);
    text-transform: none;
  }
`;

/* Under the field (FieldHint's place in the reversed group), in red */
const Problem = styled(FieldHint)`
  color: var(--color-feedback-danger);
`;

/* Amount − fee = what reaches the bank */
const Summary = styled.dl`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0;
  padding: 0.75rem 1rem;
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  line-height: 1.25rem;
  border: 1px solid var(--color-neutral-base);
  border-radius: 0.75rem;

  div {
    display: flex;
    justify-content: space-between;
  }

  dt {
    color: var(--color-neutral-150);
  }

  dd {
    margin: 0;
    color: var(--color-neutral-white);
  }

  .total {
    padding-top: 0.5rem;
    font-weight: 700;
    border-top: 1px solid var(--color-neutral-base);
  }

  .total dt {
    color: var(--color-neutral-white);
  }
`;

const TestNote = styled.p`
  padding: 0.5rem 0.75rem;
  font-family: Inter, sans-serif;
  font-size: 0.75rem;
  line-height: 1rem;
  color: #ffd00d;
  background: rgba(255, 208, 13, 0.1);
  border-radius: 0.5rem;
`;
